/**
 * Convert app locale to Strapi language parameter
 * Maps locale codes to language codes used by Strapi API
 */
export function localeToStrapiLanguage(locale: string): string {
  // Extract base language code (e.g., 'en' from 'en-US' or 'es' from 'es-ES')
  const baseLocale = locale.split('-')[0].toLowerCase();

  // Map to Strapi language codes
  const languageMap: Record<string, string> = {
    en: 'en',
    es: 'es',
    pt: 'pt',
    zh: 'zh',
    ru: 'ru',
    de: 'de',
    ja: 'ja',
    ko: 'ko',
    id: 'id',
    ar: 'ar',
  };

  return languageMap[baseLocale] || 'en';
}

/**
 * Resolve the base URL for Strapi-proxy requests across the three contexts:
 *
 * - **`next build` prerender / export** (`NEXT_PHASE === 'phase-production-build'`):
 *   the build host runs outside the cluster and cannot resolve the internal
 *   proxy, so ALWAYS use the public URL — even if `INTERNAL_*` is set in the
 *   build env. This keeps static generation from hitting cluster-only DNS.
 *   (The news zone does not fetch at build — its pages are ISR with an empty
 *   `generateStaticParams`, rendered on first request — so this branch is a
 *   safety net for the web zone's prerenders, not a requirement for news.)
 * - **Runtime server** (SSR, ISR, route handlers, sitemaps): prefer the
 *   cluster-internal address, falling back to the public one. Keeps stand
 *   traffic on the internal network.
 * - **Browser**: always the public address — internal DNS isn't reachable.
 *
 * Env contract:
 * - `NEXT_PUBLIC_API_STRAPI_PROXY_URL` — public, build-time inlined. Used by the
 *   browser AND by `next build`. MUST be a build-resolvable host.
 * - `INTERNAL_API_STRAPI_PROXY_URL` — server-only (not inlined), read at runtime.
 *   Set it to the internal cluster address only in the runtime (k8s) env; it is
 *   ignored during the build by design.
 */
export function resolveStrapiBaseUrl(): string {
  const publicUrl = process.env.NEXT_PUBLIC_API_STRAPI_PROXY_URL || '';

  // Build/prerender phase: internal DNS is unreachable — force the public host.
  if (process.env.NEXT_PHASE === 'phase-production-build') {
    return publicUrl;
  }

  // Runtime server: prefer internal, fall back to public.
  if (typeof window === 'undefined') {
    return process.env.INTERNAL_API_STRAPI_PROXY_URL || publicUrl;
  }

  // Browser.
  return publicUrl;
}
